import { expensesAPI } from '../lib/api';
import { getCurrentCompanyId, getCurrentUserId } from '../utils/auth';

// Mock data fallback for when API calls fail
const mockExpenses = [
  {
    id: 1,
    description: 'Office Supplies',
    amount: 150.00,
    category: 'Office Expenses',
    date: '2024-01-15',
    status: 'approved',
    receipt: null,
    companyId: 1,
    createdAt: '2024-01-15T10:00:00Z'
  },
  {
    id: 2,
    description: 'Business Lunch',
    amount: 85.50,
    category: 'Meals & Entertainment',
    date: '2024-01-16',
    status: 'pending',
    receipt: null,
    companyId: 1,
    createdAt: '2024-01-16T14:30:00Z'
  },
  {
    id: 3,
    description: 'Travel Expenses',
    amount: 850.00,
    category: 'Travel',
    date: '2024-01-18',
    status: 'approved',
    receipt: null,
    companyId: 1,
    createdAt: '2024-01-18T09:00:00Z'
  }
];

export const getExpenses = async (companyId = null) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    if (!actualCompanyId) {
      console.warn('No company ID available, returning empty expenses');
      return [];
    }
    
    const response = await expensesAPI.getAll(actualCompanyId);
    console.log('Expense service - Raw API response:', response.data);
    
    // Handle different API response formats
    let expenses = [];
    if (response.data) {
      if (Array.isArray(response.data)) {
        expenses = response.data;
      } else if (response.data.Success && Array.isArray(response.data.Data)) {
        expenses = response.data.Data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        expenses = response.data.data;
      } else if (response.data.success && Array.isArray(response.data.result)) {
        expenses = response.data.result;
      } else {
        console.warn('Unexpected API response format:', response.data);
        expenses = [];
      }
    }
    
    // Map API fields to expected field names
    const mappedExpenses = expenses.map(expense => ({
      id: expense.Id || expense.id,
      description: expense.Description || expense.description,
      amount: expense.Amount || expense.amount,
      category: expense.Category || expense.category,
      date: expense.ExpenseDate || expense.expenseDate || expense.date,
      status: expense.Status || expense.status,
      vendor: expense.Vendor || expense.vendor,
      paymentMethod: expense.PaymentMethod || expense.paymentMethod,
      taxDeductible: expense.IsTaxDeductible || expense.isTaxDeductible || expense.taxDeductible,
      taxAmount: expense.TaxAmount || expense.taxAmount,
      project: expense.Project || expense.project,
      receiptUrl: expense.ReceiptUrl || expense.receiptUrl,
      accountId: expense.AccountId || expense.accountId,
      companyId: expense.CompanyId || expense.companyId,
      userId: expense.UserId || expense.userId,
      createdAt: expense.CreatedAt || expense.createdAt,
      updatedAt: expense.UpdatedAt || expense.updatedAt
    }));
    
    console.log('Expense service - Processed expenses:', mappedExpenses);
    return mappedExpenses;
  } catch (error) {
    console.error('Error in getExpenses:', error);
    // Return empty array instead of mock data
    return [];
  }
};

export const createExpense = async (expenseData, accounts = []) => {
  try {
    const companyId = getCurrentCompanyId();
    const userId = getCurrentUserId();
    
    if (!userId) {
      throw new Error('User ID is required but not found. Please log in again.');
    }
    
    if (!companyId) {
      throw new Error('Company ID is required but not found. Please log in again.');
    }
    
    // Find the expense account ID based on the selected category
    const getAccountIdFromCategory = (categoryName) => {
      if (!categoryName || categoryName === 'all') return companyId; // fallback
      
      // Find the expense account that matches the category name
      const expenseAccount = accounts.find(account => 
        (account.type === 'Expense' || account.accountType === 'Expense') &&
        (account.name === categoryName || account.accountName === categoryName)
      );
      
      return expenseAccount ? (expenseAccount.id || expenseAccount.Id) : companyId;
    };
    
    // Convert account name to payment method value that database expects
    const getPaymentMethodValue = (accountName) => {
      if (!accountName) return 'cash';
      
      const name = accountName.toLowerCase();
      
      // Map common account names to database payment method values
      if (name.includes('cash') || name.includes('petty')) return 'cash';
      if (name.includes('bank') || name.includes('checking') || name.includes('savings')) return 'bank_transfer';
      if (name.includes('credit')) return 'credit_card';
      if (name.includes('debit')) return 'debit_card';
      if (name.includes('check')) return 'check';
      if (name.includes('mobile') || name.includes('mpesa') || name.includes('paypal')) return 'mobile_payment';
      
      // Default fallback
      return 'cash';
    };
    
    // Format the data according to the API schema
    const formattedData = {
      companyId: companyId,
      userId: userId,
      expenseDate: expenseData.date,
      vendor: expenseData.vendor,
      category: expenseData.category,
      amount: parseFloat(expenseData.amount),
      taxAmount: expenseData.taxAmount || 0,
      paymentMethod: getPaymentMethodValue(expenseData.paymentMethod),
      status: expenseData.status || 'pending',
      isTaxDeductible: expenseData.taxDeductible || false,
      project: expenseData.project || '',
      description: expenseData.description,
      receiptUrl: expenseData.receiptUrl || '',
      accountId: getAccountIdFromCategory(expenseData.category) // Use expense account ID
    };

    console.log('Creating expense with data:', formattedData);
    console.log('Original expenseData received:', expenseData);
    console.log('Category:', expenseData.category, '-> Account ID:', formattedData.accountId);
    console.log('Payment method:', expenseData.paymentMethod, '-> Mapped to:', formattedData.paymentMethod);
    const response = await expensesAPI.create(formattedData);
    console.log('Expense created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
};

export const updateExpense = async (id, expenseData, accounts = []) => {
  try {
    const companyId = getCurrentCompanyId();
    const userId = getCurrentUserId();
    
    if (!userId) {
      throw new Error('User ID is required but not found. Please log in again.');
    }
    
    if (!companyId) {
      throw new Error('Company ID is required but not found. Please log in again.');
    }
    
    // Find the expense account ID based on the selected category
    const getAccountIdFromCategory = (categoryName) => {
      if (!categoryName || categoryName === 'all') return companyId; // fallback
      
      // Find the expense account that matches the category name
      const expenseAccount = accounts.find(account => 
        (account.type === 'Expense' || account.accountType === 'Expense') &&
        (account.name === categoryName || account.accountName === categoryName)
      );
      
      return expenseAccount ? (expenseAccount.id || expenseAccount.Id) : companyId;
    };
    
    // Convert account name to payment method value that database expects
    const getPaymentMethodValue = (accountName) => {
      if (!accountName) return 'cash';
      
      const name = accountName.toLowerCase();
      
      // Map common account names to database payment method values
      if (name.includes('cash') || name.includes('petty')) return 'cash';
      if (name.includes('bank') || name.includes('checking') || name.includes('savings')) return 'bank_transfer';
      if (name.includes('credit')) return 'credit_card';
      if (name.includes('debit')) return 'debit_card';
      if (name.includes('check')) return 'check';
      if (name.includes('mobile') || name.includes('mpesa') || name.includes('paypal')) return 'mobile_payment';
      
      // Default fallback
      return 'cash';
    };
    
    // Format the data according to the API schema
    const formattedData = {
      companyId: companyId,
      userId: userId,
      expenseDate: expenseData.date,
      vendor: expenseData.vendor,
      category: expenseData.category,
      amount: parseFloat(expenseData.amount),
      taxAmount: expenseData.taxAmount || 0,
      paymentMethod: getPaymentMethodValue(expenseData.paymentMethod),
      status: expenseData.status || 'pending',
      isTaxDeductible: expenseData.taxDeductible || false,
      project: expenseData.project || '',
      description: expenseData.description,
      receiptUrl: expenseData.receiptUrl || '',
      accountId: getAccountIdFromCategory(expenseData.category) // Use expense account ID
    };

    console.log('Updating expense with data:', formattedData);
    console.log('Category:', expenseData.category, '-> Account ID:', formattedData.accountId);
    console.log('Payment method:', expenseData.paymentMethod, '-> Mapped to:', formattedData.paymentMethod);
    const response = await expensesAPI.update(id, formattedData);
    console.log('Expense updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating expense:', error);
    throw error;
  }
};

export const deleteExpense = async (id) => {
  try {
    await expensesAPI.delete(id);
    return true;
  } catch (error) {
    console.error('Error deleting expense:', error);
    throw error;
  }
};

export const approveExpense = async (id) => {
  try {
    const response = await expensesAPI.approve(id);
    return response.data;
  } catch (error) {
    console.error('Error approving expense:', error);
    throw error;
  }
};

export const rejectExpense = async (id) => {
  try {
    const response = await expensesAPI.reject(id);
    return response.data;
  } catch (error) {
    console.error('Error rejecting expense:', error);
    throw error;
  }
};

export const getExpensesByStatus = async (companyId = null, status) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    if (!actualCompanyId) {
      console.warn('No company ID available, returning empty expenses');
      return [];
    }
    
    const response = await expensesAPI.getByStatus(actualCompanyId, status);
    console.log('Expense service (by status) - Raw API response:', response.data);
    
    // Handle different API response formats
    let expenses = [];
    if (response.data) {
      if (Array.isArray(response.data)) {
        expenses = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        expenses = response.data.data;
      } else if (response.data.success && Array.isArray(response.data.result)) {
        expenses = response.data.result;
      } else {
        console.warn('Unexpected API response format:', response.data);
        expenses = [];
      }
    }
    
    console.log('Expense service (by status) - Processed expenses:', expenses);
    return expenses;
  } catch (error) {
    console.error('Error getting expenses by status:', error);
    // Return empty array instead of mock data
    return [];
  }
};

export const getExpensesByCategory = async (companyId = null, category) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    if (!actualCompanyId) {
      console.warn('No company ID available, using mock data');
      return mockExpenses.filter(expense => expense.category === category);
    }
    
    const response = await expensesAPI.getByCategory(actualCompanyId, category);
    return response.data || [];
  } catch (error) {
    console.error('Error getting expenses by category:', error);
    return mockExpenses.filter(expense => expense.category === category);
  }
};

export const getExpensesByDateRange = async (companyId = null, fromDate, toDate) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    if (!actualCompanyId) {
      console.warn('No company ID available, using mock data');
      return mockExpenses.filter(expense => 
        expense.date >= fromDate && expense.date <= toDate
      );
    }
    
    const response = await expensesAPI.getByDateRange(actualCompanyId, fromDate, toDate);
    return response.data || [];
  } catch (error) {
    console.error('Error getting expenses by date range:', error);
    return mockExpenses.filter(expense => 
      expense.date >= fromDate && expense.date <= toDate
    );
  }
};